// src/utils/api.js

/**
 * Выполняет fetch с автоматическими повторными попытками
 * @param {string} url URL для запроса
 * @param {object} options Опции fetch
 * @param {number} maxRetries Максимальное количество повторных попыток (по умолчанию 3)
 * @param {number} delay Начальная задержка между попытками в мс (по умолчанию 1000)
 * @returns {Promise<Response>} Ответ fetch
 */
export const fetchWithRetry = async (url, options = {}, maxRetries = 3, delay = 1000) => {
  let retries = 0;
  let lastError = null;
  // Включаем credentials для локальной разработки
  const defaultOptions = {
    credentials: 'include',
    ...options,
  };

  while (retries < maxRetries) {
    try {
      const response = await fetch(url, defaultOptions);

      if (response.ok) {
        return response;
      }

      const shouldRetry = shouldRetryOnStatus(response.status);
      if (!shouldRetry) {
        console.warn(`Не повторяем запрос для статуса ${response.status}`);
        return response;
      }

      lastError = new Error(`HTTP error! Status: ${response.status}`);
      console.warn(`Попытка ${retries + 1}/${maxRetries} HTTP ${response.status}: ${response.statusText}`);

      if (response.status === 429) {
        delay = delay * 2; // усиленный бэкофф при 429
      }
    } catch (error) {
      console.warn(`Попытка ${retries + 1}/${maxRetries} не удалась: ${error.message}`);
      lastError = error;
    }

    retries++;
    if (retries < maxRetries) {
      const backoffDelay = delay * Math.pow(1.5, retries - 1);
      console.log(`Ждем ${backoffDelay}мс перед следующей попыткой...`);
      await new Promise(resolve => setTimeout(resolve, backoffDelay));
    }
  }

  throw lastError || new Error('Превышено максимальное количество попыток');
};

/**
 * Определяет, нужно ли повторять запрос на основе HTTP статуса
 * @param {number} status HTTP статус код
 * @returns {boolean} true если нужно повторить, false если не нужно
 */
const shouldRetryOnStatus = (status) => {
  const retryableStatuses = [
    429, 500, 502, 503, 504,
    520, 521, 522, 523, 524,
  ];
  return retryableStatuses.includes(status);
};

/**
 * Проверяет доступность сервера API через /ping
 * @param {string} baseUrl Базовый URL API (например, http://localhost:8080)
 * @param {number} timeoutMs Таймаут в мс (по умолчанию 3000)
 * @returns {Promise<boolean>} true если сервер доступен, иначе false
 */
export const checkApiAvailability = async (baseUrl, timeoutMs = 3000) => {
  const controller = new AbortController();
  const timerId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    console.log('🔍 Проверяем API:', `${baseUrl}/api/index.php`);
    const response = await fetch(`${baseUrl}/api/index.php`, {
      method: 'GET',
      headers: { 'Accept': 'application/json' },
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'include',
      signal: controller.signal,
    });
    
    console.log('📡 Ответ API:', response.status, response.statusText);
    
    if (!response.ok) {
      console.error(`❌ API недоступен: HTTP ${response.status} ${response.statusText}`);
      return false;
    }
    
    // Проверяем тип контента
    const contentType = response.headers.get('content-type');
    console.log('📄 Content-Type:', contentType);
    
    if (!contentType || !contentType.includes('application/json')) {
      console.error('❌ API недоступен: Ответ не в формате JSON, получен:', contentType);
      const text = await response.text();
      console.error('Содержимое ответа:', text.substring(0, 200));
      return false;
    }
    
    const json = await response.json();
    console.log('✅ API ответ:', json);
    const isAvailable = response.ok && json.status === 'success';
    console.log('🚦 API доступен:', isAvailable);
    return isAvailable;  } catch (error) {
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      console.error('❌ API недоступен: сеть/сервер');
    } else if (error?.name === 'AbortError') {
      console.error('❌ API недоступен: Таймаут');
    } else if (error instanceof SyntaxError && error.message.includes('JSON')) {
      console.error('❌ API недоступен: Некорректный JSON ответ:', error.message);
    } else {
      console.error('❌ API недоступен:', error);
    }
    return false;
  } finally {
    clearTimeout(timerId);
  }
};

/**
 * Отладочная функция для диагностики проблем с API
 * @param {string} baseUrl Базовый URL API
 * @returns {Promise<Object>} Детальная информация об ответе API
 */
export const debugApiResponse = async (baseUrl) => {
  try {
    console.log('🔍 Отладка API:', baseUrl);
      // Сначала пробуем debug.php для детальной диагностики
    let response;
    try {
      response = await fetch(`${baseUrl}/api/debug.php`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'include',
      });
      console.log('🛠️ Тестируем debug.php...');
    } catch (debugError) {
      console.log('🛠️ debug.php недоступен, пробуем debug_simple.php...');
      try {
        response = await fetch(`${baseUrl}/api/debug_simple.php`, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
          mode: 'cors',
          cache: 'no-cache',
          credentials: 'include',
        });
      } catch (simpleDebugError) {
        console.log('🛠️ debug_simple.php недоступен, пробуем index.php...');
        response = await fetch(`${baseUrl}/api/index.php`, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
          mode: 'cors',
          cache: 'no-cache',
          credentials: 'include',
        });
      }
    }
    
    const responseInfo = {
      status: response.status,
      statusText: response.statusText,
      headers: {},
      contentType: response.headers.get('content-type'),
      url: response.url,
      ok: response.ok
    };
    
    // Собираем заголовки
    response.headers.forEach((value, key) => {
      responseInfo.headers[key] = value;
    });
    
    // Получаем содержимое как текст
    const text = await response.text();
    responseInfo.body = text;
    responseInfo.bodyLength = text.length;
    
    console.log('📊 Информация об ответе API:', responseInfo);
      // Пытаемся распарсить как JSON
    try {
      responseInfo.json = JSON.parse(text);
      console.log('✅ JSON успешно распарсен:', responseInfo.json);
    } catch (jsonError) {
      console.log('❌ Ошибка парсинга JSON:', jsonError.message);
      console.log('🔤 Полный ответ сервера (длина: ' + text.length + '):', text);
      if (text.length === 0) {
        console.log('🚨 Сервер вернул ПУСТОЙ ответ! Возможно, PHP скрипт падает с фатальной ошибкой.');
      }
    }
    
    return responseInfo;
  } catch (error) {
    console.error('💥 Ошибка при отладке API:', error);
    return { error: error.message };
  }
};

// Базовый URL для API
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? window.location.origin  // На хостинге использовать текущий домен
  : 'http://localhost:8000'; // Локально использовать порт 8000

/**
 * Авторизация пользователя по паролю
 * @param {string} password Пароль пользователя
 * @returns {Promise<Object>} Объект с данными пользователя или ошибкой
 */
export const loginUser = async (password) => {
  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/auth.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Ошибка авторизации:', error);
    throw error;
  }
};

/**
 * Загрузка файла на сервер
 * @param {File} file Файл для загрузки
 * @returns {Promise<Object>} Объект с результатом загрузки
 */
export const uploadFile = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetchWithRetry(`${API_BASE_URL}/api/files.php`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Ошибка загрузки файла:', error);
    throw error;
  }
};

/**
 * Получение списка файлов
 * @returns {Promise<Array>} Массив имен файлов
 */
export const getFilesList = async () => {
  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/files.php`, {
      method: 'GET'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Ошибка получения списка файлов:', error);
    throw error;
  }
};

/**
 * Удаление файла
 * @param {string} filename Имя файла для удаления (если не указано - удаляются все файлы)
 * @returns {Promise<string>} Сообщение о результате удаления
 */
export const deleteFile = async (filename = null) => {
  try {
    const body = filename ? JSON.stringify({ filename }) : JSON.stringify({});
    
    const response = await fetchWithRetry(`${API_BASE_URL}/api/files.php`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Ошибка удаления файла:', error);
    throw error;
  }
};

// API для работы с пакетами данных
export const getPackages = async () => {
  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/packages.php`);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Ошибка получения пакетов:', error);
    throw error;
  }
};

export const createPackage = async (packageData) => {
  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/packages.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(packageData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Ошибка создания пакета:', error);
    throw error;
  }
};

export const getSamplePackage = async () => {  try {
    const response = await fetchWithRetry(`${API_BASE_URL}/api/packages.php?sample=true`);

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.json();  } catch (error) {
    console.error('Ошибка получения образца пакета:', error);
    throw error;
  }
};

/**
 * Сохраняет обработанный документ
 * @param {object} documentData Данные документа для сохранения
 * @returns {Promise<object>} Результат сохранения
 */
export const saveProcessedDocument = async (documentData) => {
  try {
    console.log('Отправляем документ на обработку:', documentData);
    
    const response = await fetchWithRetry(`${API_BASE_URL}/api/processed-documents.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json; charset=utf-8',
      },
      body: JSON.stringify(documentData)
    });

    const result = await response.json();
    
    if (!response.ok) {
      console.error('Ошибка сохранения обработанного документа:', result);
      throw new Error(result.error || `HTTP error! Status: ${response.status}`);
    }    console.log('Документ успешно обработан:', result);
    return result;
  } catch (error) {
    console.error('Ошибка сохранения обработанного документа:', error);
    throw error;
  }
};

/**
 * Получает список обработанных документов
 * @returns {Promise<object>} Список обработанных документов
 */
export const getProcessedDocuments = async () => {
  try {
    console.log('Получаем список обработанных документов...');
    
    const response = await fetchWithRetry(`${API_BASE_URL}/api/processed-documents.php`, {
      method: 'GET'
    });

    const result = await response.json();
    
    if (!response.ok) {
      console.error('Ошибка получения обработанных документов:', result);
      throw new Error(result.error || `HTTP error! Status: ${response.status}`);
    }

    console.log('Обработанные документы получены:', result);
    return result;
  } catch (error) {
    console.error('Ошибка получения обработанных документов:', error);
    throw error;
  }
};

/**
 * Разделяет пакет на отдельные документы
 * @param {string} packageId - Идентификатор пакета для разделения
 * @returns {Promise<object>} Результат разделения пакета
 */
export const splitPackage = async (packageId) => {
  try {
    console.log('=== НАЧАЛО РАЗДЕЛЕНИЯ ПАКЕТА ===');
    console.log('PackageId:', packageId);
    console.log('NODE_ENV:', process.env.NODE_ENV);
    console.log('API_BASE_URL:', API_BASE_URL);
    console.log('Full URL:', `${API_BASE_URL}/api/split-package.php`);
    console.log('Отправляем запрос на разделение пакета:', packageId);
    
    // Используем обычный fetch для отладки
    const response = await fetch(`${API_BASE_URL}/api/split-package.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ packageId })
    });

    console.log('Получен ответ:', response.status, response.statusText);
    
    const result = await response.json();
    console.log('Данные ответа:', result);
    
    if (!response.ok) {
      console.error('Ошибка разделения пакета:', result);
      throw new Error(result.error || `HTTP error! Status: ${response.status}`);
    }

    console.log('Пакет успешно разделен:', result);
    return result;
  } catch (error) {
    console.error('Ошибка разделения пакета:', error);
    throw error;
  }
};

/**
 * Получает список документов к обработке
 * @returns {Promise<object>} Список документов к обработке
 */
export const getDocumentsToProcess = async () => {
  try {
    console.log('Получаем список документов к обработке...');
    
    const response = await fetchWithRetry(`${API_BASE_URL}/api/documents-to-process.php`, {
      method: 'GET'
    });

    const result = await response.json();
    
    if (!response.ok) {
      console.error('Ошибка получения документов к обработке:', result);
      throw new Error(result.error || `HTTP error! Status: ${response.status}`);
    }

    console.log('Документы к обработке получены:', result);
    return result;
  } catch (error) {
    console.error('Ошибка получения документов к обработке:', error);
    throw error;
  }
};

/**
 * Получает конкретный документ к обработке
 * @param {string} fileName - Имя файла документа
 * @returns {Promise<object>} Данные документа
 */
export const getDocumentToProcess = async (fileName) => {
  try {
    console.log('Получаем документ к обработке:', fileName);
    
    const response = await fetchWithRetry(`${API_BASE_URL}/api/document-to-process.php?fileName=${encodeURIComponent(fileName)}`, {
      method: 'GET'
    });

    const result = await response.json();
    
    if (!response.ok) {
      console.error('Ошибка получения документа к обработке:', result);
      throw new Error(result.error || `HTTP error! Status: ${response.status}`);
    }

    console.log('Документ к обработке получен:', result);
    return result;
  } catch (error) {
    console.error('Ошибка получения документа к обработке:', error);
    throw error;
  }
};

/**
 * Обновляет статус документа к обработке
 * @param {string} fileName - Имя файла документа
 * @param {string} status - Новый статус документа
 * @returns {Promise<object>} Результат обновления
 */
export const updateDocumentToProcessStatus = async (fileName, status) => {
  try {
    console.log('Обновляем статус документа:', fileName, 'на', status);
    
    const response = await fetchWithRetry(`${API_BASE_URL}/api/document-to-process.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fileName, status })
    });

    const result = await response.json();
    
    if (!response.ok) {
      console.error('Ошибка обновления статуса документа:', result);
      throw new Error(result.error || `HTTP error! Status: ${response.status}`);
    }

    console.log('Статус документа обновлен:', result);
    return result;
  } catch (error) {
    console.error('Ошибка обновления статуса документа:', error);
    throw error;
  }
};

/**
 * Удаляет документ к обработке
 * @param {string} fileName - Имя файла документа
 * @returns {Promise<object>} Результат удаления
 */
export const deleteDocumentToProcess = async (fileName) => {
  try {
    console.log('Удаляем документ к обработке:', fileName);
    
    const response = await fetchWithRetry(`${API_BASE_URL}/api/document-to-process.php?fileName=${encodeURIComponent(fileName)}`, {
      method: 'DELETE'
    });

    const result = await response.json();
    
    if (!response.ok) {
      console.error('Ошибка удаления документа:', result);
      throw new Error(result.error || `HTTP error! Status: ${response.status}`);
    }

    console.log('Документ удален:', result);
    return result;
  } catch (error) {
    console.error('Ошибка удаления документа:', error);
    throw error;
  }
};
